import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './BlogManagement.module.css';
import {
  FaPlus, FaCode, FaEye, FaTrash, FaCopy, FaCheck,
  FaSpinner, FaFileUpload, FaChevronLeft, FaChevronRight,
  FaMagic, FaCloudUploadAlt, FaHistory, FaCheckCircle,
  FaExclamationTriangle, FaChartLine
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';

export default function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [view, setView] = useState('dashboard'); // 'dashboard', 'editor'

  // Blog Form State
  const [currentBlog, setCurrentBlog] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [industry, setIndustry] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [status, setStatus] = useState('published'); // draft, published, scheduled

  // Editor State
  const [htmlContent, setHtmlContent] = useState('');
  const visualEditorRef = useRef(null);
  const textareaRef = useRef(null);

  // API Key State
  const [apiKey, setApiKey] = useState('');
  const [selectedLang, setSelectedLang] = useState('curl');

  // Modal & Visibility State
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [isCustomIndustry, setIsCustomIndustry] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);

  // Layout and SEO Highlight states
  const [layoutMode, setLayoutMode] = useState('split'); // 'split', 'full-visual', 'full-html'
  const [isOptimizingSeo, setIsOptimizingSeo] = useState(false);
  const [seoReport, setSeoReport] = useState(null);
  const [showSeoModal, setShowSeoModal] = useState(false);
  const [highlightedHtml, setHighlightedHtml] = useState('');
  const [isHighlightingChanges, setIsHighlightingChanges] = useState(false);

  // Importing loading state
  const [isImporting, setIsImporting] = useState(false);
  const [importingStep, setImportingStep] = useState(0);

  // Bottom action dialogs (now contain metadata settings)
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [showScheduleConfirmModal, setShowScheduleConfirmModal] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  const fileInputRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem('authData') || '{}');
  const token = userData?.token || localStorage.getItem('token');

  useEffect(() => {
    fetchBlogs();
    fetchBlogApiKey();
  }, []);

  // Sync visual editor innerHTML to state securely without losing caret focus
  useEffect(() => {
    if (view === 'editor' && visualEditorRef.current) {
      if (document.activeElement !== visualEditorRef.current) {
        if (visualEditorRef.current.innerHTML !== htmlContent) {
          visualEditorRef.current.innerHTML = htmlContent;
        }
      }
    }
  }, [view, htmlContent]);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setBlogs(res.data.blogs);
        const uniqueIndustries = [...new Set(res.data.blogs.map(b => b.industry))].filter(Boolean);
        setAvailableIndustries(uniqueIndustries);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  const fetchBlogApiKey = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/vendorapi/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && Array.isArray(res.data.data)) {
        const blogKeyObj = res.data.data.find(k => k.name === "Blog API Key");
        if (blogKeyObj && blogKeyObj.apikey !== "Not Set") {
          setApiKey(blogKeyObj.apikey);
        }
      }
    } catch (err) {
      console.error('Error fetching API key list:', err);
    }
  };

  const handleCreateNew = () => {
    setCurrentBlog(null);
    setTitle('');
    setSlug('');
    setIndustry(availableIndustries[0] || '');
    setIsCustomIndustry(availableIndustries.length === 0);
    setThumbnailUrl('');
    setMetaTitle('');
    setMetaDescription('');
    setKeywords('');
    setPublishedAt('');
    setStatus('published');
    setHtmlContent('');
    setLayoutMode('split');
    setView('editor');
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setTitle(blog.title);
    setSlug(blog.slug);
    setIndustry(blog.industry);
    setIsCustomIndustry(!availableIndustries.includes(blog.industry));
    setThumbnailUrl(blog.thumbnailUrl || '');
    setMetaTitle(blog.metaTitle || '');
    setMetaDescription(blog.metaDescription || '');
    setKeywords(blog.keywords?.join(', ') || '');
    setPublishedAt(blog.publishedAt ? new Date(blog.publishedAt).toISOString().slice(0, 16) : '');
    setStatus(blog.status);
    setHtmlContent(blog.htmlContent);
    setLayoutMode('split');
    setView('editor');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blog deleted successfully!");
      fetchBlogs();
    } catch (err) {
      toast.error('Failed to delete blog');
    }
  };

  const handleSave = async (saveStatus) => {
    if (!title.trim() || !htmlContent.trim()) {
      toast.error("Please provide at least a Blog Title and Content before saving.");
      return;
    }

    setIsSaving(saveStatus || status || 'save');
    try {
      const payload = {
        title,
        slug,
        industry: industry || 'General',
        thumbnailUrl,
        metaTitle,
        metaDescription,
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k),
        publishedAt: publishedAt || Date.now(),
        status: saveStatus || status,
        htmlContent
      };

      if (currentBlog) {
        await axios.put(`${API_BASE_URL}/api/blogs/${currentBlog._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/api/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog created successfully!");
      }

      fetchBlogs();
      setView('dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to save blog');
    } finally {
      setIsSaving(false);
    }
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file) return;
    setIsImporting(true);
    setImportingStep(0);

    // Elegant Multi-step Loading text carousel
    const steps = [
      "Reading document contents...",
      "Extracting HTML headers & structure...",
      "Analyzing body formatting & tags...",
      "Compiling layout parameters...",
      "Writing SEO drafts...",
      "Almost ready..."
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setImportingStep(currentStep);
      } else {
        clearInterval(interval);

        const reader = new FileReader();
        reader.onload = (evt) => {
          const result = evt.target.result;
          setHtmlContent(result);

          // Parse HTML and automatically extract first h1 tag text as title!
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(result, "text/html");
            const h1Element = doc.querySelector("h1");
            if (h1Element && h1Element.textContent) {
              const extractedTitle = h1Element.textContent.trim();
              setTitle(extractedTitle);
              setMetaTitle(extractedTitle);

              // Generate URL slug from title
              const generatedSlug = extractedTitle
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              setSlug(generatedSlug);
            }
          } catch (e) {
            console.error("Error parsing title from imported HTML:", e);
          }

          setIsImporting(false);
          toast.success("Document imported successfully!");
        };
        reader.readAsText(file);
      }
    }, 500);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
    e.target.value = ''; // Reset input
  };

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(getSnippet());
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  const handleCopyApiKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopiedApiKey(true);
    setTimeout(() => setCopiedApiKey(false), 2000);
  };

  const handleVisualEdit = (e) => {
    setHtmlContent(e.currentTarget.innerHTML);
  };

  // Match Visual Selection to Raw HTML Code Textarea
  const handleVisualSelection = () => {
    const selection = window.getSelection();
    if (!selection) return;

    const selectedText = selection.toString().trim();
    if (selectedText.length >= 3) {
      const index = htmlContent.indexOf(selectedText);
      if (index !== -1) {
        if (textareaRef.current) {
          // If HTML text editor is visible
          if (layoutMode === 'split' || layoutMode === 'full-html') {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(index, index + selectedText.length);

            // Calculate scroll offset based on character height
            const textBeforeSelection = htmlContent.substring(0, index);
            const lines = textBeforeSelection.split('\n').length;
            textareaRef.current.scrollTop = (lines - 4) * 20; // 20px line-height
          }
        }
      }
    }
  };

  // AI SEO Optimization
  const handleOptimizeSeo = async () => {
    if (!htmlContent.trim()) {
      toast.error("Please enter or import content before running SEO optimizer.");
      return;
    }
    setIsOptimizingSeo(true);
    try {
      const payload = {
        title: title || 'Untitled Blog',
        htmlContent,
        metaTitle,
        metaDescription,
        keywords,
        industry: industry || 'General'
      };
      const res = await axios.post(`${API_BASE_URL}/api/blogs/optimize-seo`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSeoReport(res.data.data);
        setShowSeoModal(true);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "SEO Optimization failed. Please check AI credentials.");
    } finally {
      setIsOptimizingSeo(false);
    }
  };

  const escapeHtml = (text) => {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const applySeoOptimizations = () => {
    if (!seoReport) return;

    const optimizedContent = seoReport.optimizedHtmlContent;

    // Prepare escaped text for raw HTML highlight preview
    let tempHighlighted = escapeHtml(optimizedContent);

    if (Array.isArray(seoReport.changes)) {
      seoReport.changes.forEach(change => {
        const optSnippet = change.optimizedSnippet || change.optimized || change.newSnippet;
        if (optSnippet) {
          const escapedSnippet = escapeHtml(optSnippet);
          const highlightStart = `<span class="raw-html-highlight-${change.type}">`;
          const highlightEnd = `</span>`;
          tempHighlighted = tempHighlighted.replaceAll(
            escapedSnippet,
            `${highlightStart}${escapedSnippet}${highlightEnd}`
          );
        }
      });
    }

    setHighlightedHtml(tempHighlighted);
    setIsHighlightingChanges(true);
    setLayoutMode('split'); // Ensure split layout to show the highlighted HTML side-by-side

    // Extract optimized titles, slugs, and descriptions with rich fallback mappings
    const newTitle = seoReport.optimizedTitle || seoReport.title || seoReport.optimizedMetaTitle || title;
    const newSlug = seoReport.optimizedSlug || seoReport.slug || (newTitle ? newTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") : slug);
    const newMetaTitle = seoReport.optimizedMetaTitle || seoReport.metaTitle || newTitle;
    const newMetaDesc = seoReport.optimizedMetaDescription || seoReport.metaDescription || seoReport.description || metaDescription;

    // Apply values including optimized Title and optimized Slug
    setTitle(newTitle);
    setSlug(newSlug);
    setMetaTitle(newMetaTitle);
    setMetaDescription(newMetaDesc);

    if (Array.isArray(seoReport.optimizedKeywords)) {
      setKeywords(seoReport.optimizedKeywords.join(', '));
    }
    setHtmlContent(optimizedContent);
    setShowSeoModal(false);
    toast.success("AI SEO Optimizations applied!");
  };

  const getSnippet = () => {
    const externalHost = API_BASE_URL || "https://api.toxbox.com";
    const key = apiKey || "YOUR_GENERATED_API_KEY";
    switch (selectedLang) {
      case 'curl':
        return `curl -X GET "${externalHost}/api/blogs/external?page=1&limit=10" \\\n  -H "X-toxbox-api-key: ${key}"`;
      case 'node':
        return `const axios = require('axios');\naxios.get('${externalHost}/api/blogs/external?slug=my-blog', {\n  headers: { 'X-toxbox-api-key': '${key}' }\n}).then(res => console.log(res.data));`;
      case 'python':
        return `import requests\nheaders = {'X-toxbox-api-key': '${key}'}\nres = requests.get('${externalHost}/api/blogs/external?page=1', headers=headers)\nprint(res.json())`;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2>Blog Management</h2>
        </div>
        {view === 'dashboard' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className={styles.secondaryBtn} onClick={() => setShowIntegrationModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCode /> API Integration
            </button>
            <button className={styles.primaryBtn} onClick={handleCreateNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPlus /> Create Blog
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button className={styles.secondaryBtn} onClick={() => setView('dashboard')}>
              Cancel
            </button>

            {htmlContent.trim() && (
              <>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => fileInputRef.current?.click()}
                  title="Import different HTML or Text document"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '40px' }}
                >
                  <FaFileUpload /> Re-Import Doc
                </button>

                <button
                  type="button"
                  onClick={handleOptimizeSeo}
                  disabled={isOptimizingSeo}
                  className={styles.primaryBtn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                    boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.1)'
                  }}
                >
                  {isOptimizingSeo ? (
                    <><FaSpinner className={styles.spinAnimation} /> Analyzing...</>
                  ) : (
                    <><FaMagic /> Optimize SEO with AI</>
                  )}
                </button>

                <button className={styles.secondaryBtn} onClick={() => handleSave('draft')} disabled={isSaving} style={{ height: '40px', fontWeight: '600' }}>
                  {isSaving === 'draft' ? 'Saving...' : 'Save Draft'}
                </button>

                <button className={styles.secondaryBtn} onClick={() => setShowScheduleConfirmModal(true)} disabled={isSaving} style={{ height: '40px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: '600' }}>
                  Schedule
                </button>

                <button className={styles.primaryBtn} onClick={() => setShowPublishConfirmModal(true)} disabled={isSaving} style={{ height: '40px', background: '#10b981', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)', fontWeight: '600' }}>
                  Publish Now
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {view === 'dashboard' && (
        <>
          <div className={styles.grid}>
            {blogs.map(blog => (
              <div key={blog._id} className={`${styles.card} ${blog.status === 'draft' ? styles.draftCard : ''}`}>
                {blog.thumbnailUrl && <img src={blog.thumbnailUrl} alt="Thumbnail" className={styles.cardImage} />}
                <div className={styles.cardBody}>
                  <div className={styles.cardBadges}>
                    <span className={`${styles.badge} ${styles[blog.status]}`}>{blog.status}</span>
                    <span className={styles.badgeIndustry}>{blog.industry}</span>
                  </div>
                  <h3>{blog.title}</h3>
                  <small>Slug: {blog.slug}</small>
                  {blog.status === 'scheduled' && (
                    <div className={styles.scheduledAlert}>
                      Scheduled for: {new Date(blog.publishedAt).toLocaleString()}
                    </div>
                  )}
                  <div className={styles.cardActions}>
                    <button onClick={() => setPreviewBlog(blog)} className={styles.previewBtn} title="Preview Blog">
                      <FaEye />
                    </button>
                    <button onClick={() => handleEdit(blog)} className={styles.editBtn} title="Edit Blog">Edit</button>
                    <button onClick={() => handleDelete(blog._id)} className={styles.deleteBtn} title="Delete Blog"><FaTrash /></button>
                  </div>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <p>No blogs found. Create one to get started!</p>}
          </div>
        </>
      )}

      {view === 'editor' && (
        <div className={styles.editorLayout}>
          <div className={styles.editorMain} style={{ width: '100%' }}>
            <input
              type="file"
              accept=".html,.txt"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileUpload}
            />

            {!htmlContent.trim() ? (
              <div className={styles.uploadPlaceholderWrapper}>
                <div
                  className={styles.uploadPlaceholder}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className={styles.uploadIconContainer}>
                    <FaCloudUploadAlt className={styles.uploadIcon} />
                  </div>
                  <h3>Import HTML or Text Document</h3>
                  <p>Drag and drop your file here, or click to browse</p>
                  <span className={styles.uploadBadge}>Supports .html, .txt</span>
                </div>
              </div>
            ) : (
              <>
                {/* Side-by-side Split View Workspace */}
                <div className={styles.editorAreaSplit}>
                  {/* Left Column: Visual Preview */}
                  {(layoutMode === 'split' || layoutMode === 'full-visual') && (
                    <div className={`${styles.splitPanel} ${layoutMode === 'full-visual' ? styles.fullPanel : ''}`}>
                      <div className={styles.panelHeader}>
                        <span>Visual Preview (WYSIWYG)</span>
                        <button
                          type="button"
                          onClick={() => setLayoutMode(layoutMode === 'full-visual' ? 'split' : 'full-visual')}
                          className={styles.panelToggleBtn}
                        >
                          {layoutMode === 'full-visual' ? "Split View" : "Full Screen"}
                        </button>
                      </div>
                      <div
                        className={styles.visualEditor}
                        contentEditable={true}
                        ref={visualEditorRef}
                        onInput={handleVisualEdit}
                        onMouseUp={handleVisualSelection}
                        onKeyUp={handleVisualSelection}
                      />
                    </div>
                  )}

                  {/* Right Column: Raw HTML Editor / Highlight changes */}
                  {(layoutMode === 'split' || layoutMode === 'full-html') && (
                    <div className={`${styles.splitPanel} ${layoutMode === 'full-html' ? styles.fullPanel : ''}`}>
                      <div className={styles.panelHeader}>
                        <span>Raw HTML Editor {isHighlightingChanges && "(AI Optimization Highlights)"}</span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {isHighlightingChanges && (
                            <button
                              type="button"
                              onClick={() => setIsHighlightingChanges(false)}
                              className={styles.panelToggleBtn}
                              style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fecaca', marginRight: '8px' }}
                            >
                              Edit Raw HTML
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setLayoutMode(layoutMode === 'full-html' ? 'split' : 'full-html')}
                            className={styles.panelToggleBtn}
                          >
                            {layoutMode === 'full-html' ? "Split View" : "Full Screen"}
                          </button>
                        </div>
                      </div>

                      <div className={styles.textareaWrapper}>
                        {isHighlightingChanges ? (
                          <pre
                            className={styles.htmlHighlightView}
                            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                          />
                        ) : (
                          <textarea
                            ref={textareaRef}
                            className={styles.htmlTextarea}
                            value={htmlContent}
                            onChange={(e) => setHtmlContent(e.target.value)}
                            placeholder="Paste or write your HTML here..."
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* importing Loader overlay */}
      {isImporting && createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.importingCard}>
            <div className={styles.blogLoaderIcon}>
              <FaFileUpload className={styles.spinAnimation} style={{ color: '#6366f1', fontSize: '32px' }} />
            </div>
            <h3>Importing Your Blog Document</h3>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar} style={{ width: `${(importingStep + 1) * 16.6}%` }} />
            </div>
            <p className={styles.importingStepText}>
              {["Reading document contents...", "Extracting HTML headers & structure...", "Analyzing body formatting & tags...", "Compiling layout parameters...", "Writing SEO drafts...", "Almost ready..."][importingStep]}
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* SEO Modal (rendered in body via portal) */}
      {showSeoModal && seoReport && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowSeoModal(false)}>
          <div className={styles.seoModalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.seoHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FaMagic style={{ color: '#8b5cf6', fontSize: '24px' }} />
                <h3>AI SEO Audit Report</h3>
              </div>
              <button className={styles.closeBtn} onClick={() => setShowSeoModal(false)}>&times;</button>
            </div>

            <div className={styles.seoScoreSection}>
              <div className={styles.seoGauge}>
                <div className={styles.seoGaugeCircle} style={{ '--score': seoReport.seoScore }}>
                  <span>{seoReport.seoScore}</span>
                  <small>Overall</small>
                </div>
              </div>

              <div className={styles.seoBreakdownDashboard}>
                <h4>SEO Score Breakdown</h4>
                {seoReport.scoreBreakdown ? (
                  <div className={styles.breakdownGrid}>
                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Heading Hierarchy</span>
                        <strong>{seoReport.scoreBreakdown.headers || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.headers || 0}%`, background: '#8b5cf6' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Image Alt Tagging</span>
                        <strong>{seoReport.scoreBreakdown.images || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.images || 0}%`, background: '#3b82f6' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Keyword Placements</span>
                        <strong>{seoReport.scoreBreakdown.keywords || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.keywords || 0}%`, background: '#10b981' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Readability Flow</span>
                        <strong>{seoReport.scoreBreakdown.readability || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.readability || 0}%`, background: '#f59e0b' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Metadata Formatting</span>
                        <strong>{seoReport.scoreBreakdown.meta || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.meta || 0}%`, background: '#ec4899' }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>Check the list below for specific optimization summaries.</p>
                )}
              </div>
            </div>

            <div className={styles.seoDetailsGrid}>
              <div className={styles.seoSection}>
                <h4>Optimizations Applied</h4>
                <ul className={styles.seoReportList}>
                  {seoReport.report?.map((item, i) => (
                    <li key={i}><FaCheckCircle className={styles.successIcon} /> {item}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.seoSection}>
                <h4>Changes Highlighted</h4>
                <div className={styles.seoDiffContainer}>
                  {seoReport.changes?.map((change, i) => {
                    const origText = change.originalSnippet || change.original || change.oldSnippet || "";
                    const optText = change.optimizedSnippet || change.optimized || change.newSnippet || "";
                    return (
                      <div key={i} className={styles.seoDiffItem}>
                        <div className={styles.diffHeader}>
                          <span className={`${styles.diffType} ${styles[change.type]}`}>{change.type}</span>
                          <p>{change.description}</p>
                        </div>
                        {origText && (
                          <div className={styles.diffOriginal}>
                            <small className={styles.diffLabel}>Original Code/Content:</small>
                            <pre>{origText}</pre>
                          </div>
                        )}
                        {optText && (
                          <div className={styles.diffOptimized}>
                            <small className={styles.diffLabel}>Optimized Code/Content:</small>
                            <pre>{optText}</pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.seoModalActions}>
              <button onClick={() => setShowSeoModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button onClick={applySeoOptimizations} className={styles.primaryBtn}>
                <FaCheck /> Apply Changes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Publish Now Confirm & Settings Modal (rendered in body via portal) */}
      {showPublishConfirmModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowPublishConfirmModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Publish Settings & Metadata</h3>
              <button className={styles.closeBtn} onClick={() => setShowPublishConfirmModal(false)}>&times;</button>
            </div>

            <div className={styles.modalBodyScrollable}>
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Blog Title *</label>
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="E.g., Future of AI in B2B Customer Support" />
                </div>
                <div className={styles.formGroup}>
                  <label>Slug (URL path)</label>
                  <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="Leave blank to auto-generate" />
                </div>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  {!isCustomIndustry && availableIndustries.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        style={{ flexGrow: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="">Select an Industry...</option>
                        {availableIndustries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => { setIsCustomIndustry(true); setIndustry(''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                        + New
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        placeholder="E.g., Healthcare or Finance"
                        style={{ flexGrow: 1 }}
                      />
                      {availableIndustries.length > 0 && (
                        <button type="button" onClick={() => { setIsCustomIndustry(false); setIndustry(availableIndustries[0] || ''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Thumbnail URL</label>
                  <input type="text" value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} placeholder="https://..." />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '14px', fontWeight: '700' }}>Search Engine Optimization (SEO)</h4>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Meta Title</label>
                  <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder="SEO Meta Title" />
                </div>
                <div className={styles.formGroup}>
                  <label>Keywords (comma separated)</label>
                  <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="SEO, marketing, blogs" />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Meta Description</label>
                  <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} placeholder="SEO description..." style={{ minHeight: '60px' }} />
                </div>
              </div>
            </div>

            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowPublishConfirmModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button
                onClick={() => {
                  if (!title.trim()) {
                    toast.error("Please enter a Blog Title.");
                    return;
                  }
                  setShowPublishConfirmModal(false);
                  handleSave('published');
                }}
                className={styles.primaryBtn}
                style={{ background: '#10b981' }}
              >
                Confirm & Publish
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Schedule Confirm & Settings Modal (rendered in body via portal) */}
      {showScheduleConfirmModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowScheduleConfirmModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Schedule Settings & Metadata</h3>
              <button className={styles.closeBtn} onClick={() => setShowScheduleConfirmModal(false)}>&times;</button>
            </div>

            <div className={styles.modalBodyScrollable}>
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label style={{ color: '#4f46e5', fontWeight: '800' }}>Publish Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={publishedAt}
                    onChange={e => setPublishedAt(e.target.value)}
                    style={{ marginTop: '6px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #6366f1', fontWeight: '600' }}
                  />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />

              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Blog Title *</label>
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="E.g., Future of AI in B2B Customer Support" />
                </div>
                <div className={styles.formGroup}>
                  <label>Slug (URL path)</label>
                  <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="Leave blank to auto-generate" />
                </div>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  {!isCustomIndustry && availableIndustries.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        style={{ flexGrow: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="">Select an Industry...</option>
                        {availableIndustries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => { setIsCustomIndustry(true); setIndustry(''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                        + New
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        placeholder="E.g., Healthcare or Finance"
                        style={{ flexGrow: 1 }}
                      />
                      {availableIndustries.length > 0 && (
                        <button type="button" onClick={() => { setIsCustomIndustry(false); setIndustry(availableIndustries[0] || ''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Thumbnail URL</label>
                  <input type="text" value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} placeholder="https://..." />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '14px', fontWeight: '700' }}>Search Engine Optimization (SEO)</h4>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Meta Title</label>
                  <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder="SEO Meta Title" />
                </div>
                <div className={styles.formGroup}>
                  <label>Keywords (comma separated)</label>
                  <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="SEO, marketing, blogs" />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Meta Description</label>
                  <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} placeholder="SEO description..." style={{ minHeight: '60px' }} />
                </div>
              </div>
            </div>

            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowScheduleConfirmModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button
                onClick={() => {
                  if (!publishedAt) {
                    toast.error("Please select a date and time to schedule.");
                    return;
                  }
                  if (!title.trim()) {
                    toast.error("Please enter a Blog Title.");
                    return;
                  }
                  setShowScheduleConfirmModal(false);
                  handleSave('scheduled');
                }}
                className={styles.primaryBtn}
                style={{ background: '#3b82f6' }}
              >
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* API Integration Modal */}
      {showIntegrationModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowIntegrationModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>External API Integration</h3>
              <button className={styles.closeBtn} onClick={() => setShowIntegrationModal(false)}>&times;</button>
            </div>
            <div className={styles.modalBodyScrollable}>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>
                Use your secure API key to display these blogs on your own website or platform.
              </p>

              <div className={styles.formGroup}>
                <label>Language Selector</label>
                <select value={selectedLang} onChange={e => setSelectedLang(e.target.value)} className={styles.selectInput} style={{ width: '100%', marginBottom: '16px' }}>
                  <option value="curl">cURL</option>
                  <option value="node">Node.js</option>
                  <option value="python">Python</option>
                </select>
              </div>

              {apiKey ? (
                <div className={styles.apiKeyAlert} style={{ marginBottom: '16px' }}>
                  <div className={styles.apiKeyHeader}>
                    <strong>Active Blog API Key Assigned</strong>
                  </div>
                  <div className={styles.apiSecretContainer}>
                    <input
                      type="text"
                      value={apiKey}
                      readOnly
                      className={styles.apiSecretInput}
                    />
                    <button className={styles.iconBtn} onClick={handleCopyApiKey} title="Copy Key">
                      {copiedApiKey ? <FaCheck style={{ color: '#10b981' }} /> : <FaCopy />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.noApiKeyWarning} style={{ marginBottom: '16px' }}>
                  <FaExclamationTriangle />
                  <span>No Blog API key assigned yet. Please contact your administrator.</span>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Code Snippet</label>
                <div className={styles.codeSnippet} style={{ marginTop: '6px' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{getSnippet()}</pre>
                  <button className={styles.copyBtn} onClick={handleCopySnippet} style={{ background: copiedSnippet ? '#064e3b' : 'rgba(255,255,255,0.1)', color: copiedSnippet ? '#34d399' : 'white', border: copiedSnippet ? '1px solid #059669' : '1px solid rgba(255,255,255,0.15)' }}>
                    {copiedSnippet ? <><FaCheck style={{ marginRight: '6px' }} /> Copied!</> : 'Copy Snippet'}
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowIntegrationModal(false)} className={styles.secondaryBtn}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewBlog && createPortal(
        <div className={styles.modalOverlay} onClick={() => setPreviewBlog(null)}>
          <div className={styles.previewModal} onClick={e => e.stopPropagation()}>
            <button className={styles.closePreviewBtn} onClick={() => setPreviewBlog(null)}>&times;</button>
            <div className={styles.previewModalHeader}>
              <h2>{previewBlog.title}</h2>
              <span className={styles.badgeIndustry}>{previewBlog.industry || 'General'}</span>
            </div>
            <div className={styles.previewModalContent} dangerouslySetInnerHTML={{ __html: previewBlog.htmlContent }} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
